import { useEffect, useRef, useState } from "react";
import AlertPopup from "../../../../components/common/alertPopup/AlertPopup";
import CustomButton from "../../../../components/common/button/CustomButton";
import { useDispatch } from "react-redux";
import {
  getLeadEstimateLinkAction,
  setLeadEstimateDownloaded,
  setSingleLeadEstimateAction,
  setUpdateLeadAction,
  addLeadQuoteAction,
  setUpdateLeadQuoteResponse,
  getLeadEstimateAction,
  getPreviousQuoteAction,
} from "../../../../store/reducers/leadSlice";
import { useSelector } from "react-redux";
import AlertMessage from "./AlertMessage";
import ManageDepositInvoice from "../../invoice/ManageDepositInvoice";
import SplitTemplateButton from "../SplitTemplateButton";
import {
  getAllEmailTemplatesAction,
  leadSendEmailTemplateAction,
} from "../../../../store/reducers/emailTemplateSlice";

// ─── Popup content maps ────────────────────────────────────────────────────────
const popupTitle: any = {
  email: "Lead Estimate Email",
  pdf: "Lead Estimate PDF",
  odt: "Lead Estimate ODT",
};

const popupTitleQuote: any = {
  email: "Lead Quote Email",
  pdf: "Lead Quote PDF",
  odt: "Lead Quote ODT",
  previous: "Lead Previous Quote",
};

const popupContent: any = {
  email: "Are you sure you want to send an email?",
  pdf: "Are you sure you want to download the PDF?",
  odt: "Are you sure you want to download the ODT?",
  previous: "Are you sure you want to download it?",
};

const saveWarningContent: any = {
  email: "Please save the quote data before sending an email.",
  pdf: "Please save the quote data before generating a PDF.",
  odt: "Please save the quote data before generating an ODT.",
};

// ─── Props ─────────────────────────────────────────────────────────────────────
interface EstimateActionsPopupProps {
  estimateForm: any;
  screenType?: any;
  isShowActionCalled?: any;
  setIsShowActionCalled?: any;
  disabledField?: any;
  setIsFieldValueChanged?: any;
  isFieldValueChanged?: any;
  activeTab?: number;
  quoteId?: string | null;
  handleDuplicateTab?: () => void;
}

// ─── Component ─────────────────────────────────────────────────────────────────
const QuoteActions: React.FC<EstimateActionsPopupProps> = ({
  estimateForm,
  screenType = "",
  isShowActionCalled,
  setIsShowActionCalled,
  disabledField,
  isFieldValueChanged,
  setIsFieldValueChanged,
  activeTab = 0,
  quoteId = null,
  handleDuplicateTab,
}) => {
  const [open, setOpen] = useState(false);
  const [invoiceModel, setInvoiceModel] = useState(false);
  const [warningModelShow, setWarningModelShow] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  // ── actionType: useState for UI rendering + useRef for effect closures ───────
  // ISSUE 1: actionType inside useEffect was stale (not in deps array).
  // Fix: mirror it into a ref so effects always read the latest value.
  const [actionType, setActionType] = useState("");
  const actionTypeRef = useRef<string>("");

  // ── selectedTemplate: useRef only (no useState needed) ──────────────────────
  // ISSUE 2: useState is async — calling setSelectedTemplate() then onShow()
  // immediately meant onShow read null on the first click.
  // Fix: useRef updates synchronously, value is always current in same tick.
  const selectedTemplateRef = useRef<any>(null);

  // ─── Selectors ────────────────────────────────────────────────────────────────

  const { userProfile } = useSelector((state: any) => state.user);
  const {
    leadDetails,
    isFileDownloaded,
    previousQuotes,
    isUpdateLeadSuccess,
    updateLeadQuoteResponse,
  } = useSelector((state: any) => state.lead);
  const { allEmailTemplates } = useSelector(
    (state: any) => state.emailTemplates,
  );
  const dispatch = useDispatch();

  // state add karo
  const [previousQuoteOpen, setPreviousQuoteOpen] = useState(false);
  const [selectedPreviousQuote, setSelectedPreviousQuote] = useState<any>(null);
  const [openUpward, setOpenUpward] = useState(false); // ✅ naya state

  const previousQuoteRef = useRef<HTMLDivElement>(null);
  const previousQuoteBtnRef = useRef<HTMLDivElement>(null); // ✅ button wrapper ka ref, position check ke liye

  // Use correct professional plan key
  const isProfessional = userProfile?.features?.includes("Professional Quotes");

  // Fetch templates only when professional
  useEffect(() => {
    if (isProfessional) {
      dispatch(getAllEmailTemplatesAction({ lead_stage: "QUOTE" }));
    }
  }, [dispatch, isProfessional]);

  // ─── Close on file downloaded ─────────────────────────────────────────────────
  useEffect(() => {
    if (isFileDownloaded) {
      dispatch(setLeadEstimateDownloaded(false));
      onHide();
      dispatch(
        getLeadEstimateAction({
          generatedId: leadDetails.lead.generated_id,
          screenType,
        }),
      );
    }
  }, [isFileDownloaded]);

  // ─── Close dropdown on outside click ──────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        previousQuoteRef.current &&
        !previousQuoteRef.current.contains(event.target as Node)
      ) {
        setPreviousQuoteOpen(false);
      }
    };
    if (previousQuoteOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [previousQuoteOpen]);

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const onHide = () => {
    setOpen(false);
    setWarningModelShow(false);
    setActionType("");
    actionTypeRef.current = "";
  };

  const getCurrentQuoteId = () => quoteId || estimateForm?.quoteId;

  // ─── Set actionType in both state and ref ─────────────────────────────────────
  const updateActionType = (type: string) => {
    setActionType(type);
    actionTypeRef.current = type;
  };

  // Helper for sending email (matches EstimateActions style)
  const handleSendEmailWithTemplate = (template: any) => {
    dispatch(
      leadSendEmailTemplateAction({
        templateId: template?.template_id || "",
        leadId: leadDetails?.lead?.generated_id || "",
        quoteId: getCurrentQuoteId() || "",
        to: leadDetails?.lead?.email || "",
        template_from: template?.template_from,
        cc: [],
        bcc: [],
      }),
    );
    // After sending email, refresh the quote
    if (leadDetails?.lead?.generated_id) {
      dispatch(
        getLeadEstimateAction({
          generatedId: leadDetails.lead.generated_id,
          screenType,
        }),
      );
    }
  };

  // Send email: professional → template API, non-pro → standard API
  const sendEmail = (template?: any) => {
    const currentQuoteId = getCurrentQuoteId();
    const resolvedTemplate = template || selectedTemplateRef.current;

    if (isProfessional && resolvedTemplate) {
      handleSendEmailWithTemplate(resolvedTemplate);
      selectedTemplateRef.current = null;
    } else {
      dispatch(
        getLeadEstimateLinkAction({
          leadId: leadDetails?.lead?.generated_id,
          estimateId:
            screenType === "quotation"
              ? currentQuoteId
              : estimateForm?.estimateId,
          type: "email",
          screenType,
        }),
      );
      // After sending email, refresh the quote
      if (leadDetails?.lead?.generated_id) {
        dispatch(
          getLeadEstimateAction({
            generatedId: leadDetails.lead.generated_id,
            screenType,
          }),
        );
      }
    }
    selectedTemplateRef.current = null;
    actionTypeRef.current = "";
  };

  // ─── Standard submit for PDF / ODT ────────────────────────────────────────────
  const submitHandler = (typeParam?: string) => {
    // ISSUE 5: typeParam || actionType — actionType was stale in effect closures.
    // Fix: fall back to actionTypeRef.current which is always current.
    const finalType = typeParam || actionTypeRef.current || actionType;
    const currentQuoteId = getCurrentQuoteId();

    dispatch(
      getLeadEstimateLinkAction({
        leadId: leadDetails?.lead?.generated_id,
        estimateId:
          screenType === "quotation"
            ? currentQuoteId
            : estimateForm?.estimateId,
        type: finalType,
        screenType,
      }),
    );

    setIsShowActionCalled(false);
    dispatch(setSingleLeadEstimateAction(false));
    updateActionType("");
  };

  // ─── Main action trigger ──────────────────────────────────────────────────────
  // template param is passed DIRECTLY — never rely on state for this
  const onShow = (type: string, template?: any) => {
    // ✅ Store template synchronously in ref before anything async happens
    if (template) {
      selectedTemplateRef.current = template;
    }

    updateActionType(type); // sync update to both state + ref

    const currentQuoteId = getCurrentQuoteId();
    const isQuotation = screenType === "quotation" && !!currentQuoteId;
    const isNotQuotation =
      screenType !== "quotation" && !!estimateForm?.estimateId;
    const hasExistingRecord = isQuotation || isNotQuotation;

    if (isFieldValueChanged && hasExistingRecord) {
      // ── Has unsaved changes → save first, then fire via isUpdateLeadSuccess ──
      setIsShowActionCalled(true);
    } else if (!isFieldValueChanged && hasExistingRecord) {
      // ── Already saved → fire immediately ────────────────────────────────────
      if (type === "email") {
        sendEmail(template);
      } else {
        submitHandler(type);
      }
    } else {
      // ── No existing record → trigger save first ──────────────────────────────
      setIsShowActionCalled(true);
    }
  };

  // ─── After save completes → fire the pending action ───────────────────────────
  // ISSUE 6: was reading actionType (state) and selectedTemplate (state) inside
  // this effect — both were stale because neither was in the deps array.
  // Fix: read from refs which are always current regardless of closure age.
  useEffect(() => {
    if (
      updateLeadQuoteResponse &&
      actionTypeRef.current &&
      estimateForm?.quoteId
    ) {
      const currentType = actionTypeRef.current; // ✅ always fresh
      const currentTemplate = selectedTemplateRef.current; // ✅ always fresh

      if (currentType === "email") {
        // Email action — delegate to sendEmail which handles pro vs non-pro
        sendEmail(currentTemplate);
      } else {
        // PDF / ODT
        submitHandler(currentType);
      }

      setIsShowActionCalled(false);
      dispatch(setUpdateLeadAction(false));
      dispatch(setUpdateLeadQuoteResponse(false));
      dispatch(setSingleLeadEstimateAction(false));
      updateActionType("");
    }
  }, [updateLeadQuoteResponse, quoteId, estimateForm?.quoteId]); // refs don't need to be in deps — they don't trigger re-renders

  // ─── Accept quote ──────────────────────────────────────────────────────────────
  const handleAcceptQuote = () => {
    const currentQuoteId = getCurrentQuoteId();
    const isQuotation = screenType === "quotation" && !!currentQuoteId;
    const isNotQuotation =
      screenType !== "quotation" && !!estimateForm?.estimateId;

    if (!isQuotation && !isNotQuotation) {
      setWarningModelShow(true);
      return;
    }

    const updatedServices = (estimateForm?.services || []).map(
      (service: any) => {
        let vehicles: any[] = [];

        if (Array.isArray(service?.vehicles)) {
          vehicles = service.vehicles
            .map((v: any, vIdx: number) => ({
              vehicleTypeId: v?.vehicleTypeId ?? "",
              vehicleQty: Number(v?.vehicleQty) || 0,
              fuelCharge: Number(v?.fuelCharge) || 0,
              orderIndex: v?.orderIndex ?? vIdx,
            }))
            .filter(
              (v: any) =>
                v.vehicleTypeId || v.vehicleQty > 0 || v.fuelCharge > 0,
            );
        } else {
          const hasVehicleData =
            service?.vehicleTypeId ||
            Number(service?.vehicleQty) > 0 ||
            Number(service?.fuelCharge) > 0;

          if (hasVehicleData) {
            vehicles = [
              {
                vehicleTypeId: service?.vehicleTypeId ?? "",
                vehicleQty: Number(service?.vehicleQty) || 0,
                fuelCharge: Number(service?.fuelCharge) || 0,
                orderIndex: 0,
              },
            ];
          }
        }

        const { vehicleQty, vehicleTypeId, fuelCharge, ...rest } = service;
        return {
          ...rest,
          price: Number(service?.price) || 0,
          driverQty: Number(service?.driverQty) || 0,
          porterQty: Number(service?.porterQty) || 0,
          packerQty: Number(service?.packerQty) || 0,
          wageCharge: Number(service?.wageCharge) || 0,
          vehicles: vehicles || [],
        };
      },
    );

    dispatch(
      addLeadQuoteAction({
        body: {
          ...estimateForm,
          services: updatedServices,
          accepted_status: "Accepted",
        },
        generatedId: leadDetails?.lead?.generated_id,
        screenType,
      }),
    );

    setIsFieldValueChanged(false);
  };

  // ─── handlePreviousQuoteClick me position check add karo ─────────────────────
  const handlePreviousQuoteClick = async () => {
    const currentQuoteId = getCurrentQuoteId();

    if (!currentQuoteId) {
      return;
    }

    // ✅ Decide upward vs downward before opening
    if (previousQuoteBtnRef.current) {
      const rect = previousQuoteBtnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const estimatedListHeight = 260; // approx max dropdown height (matches maxHeight below)
      setOpenUpward(spaceBelow < estimatedListHeight);
    }

    setPreviousQuoteOpen(true);
    dispatch(
      getPreviousQuoteAction({
        leadId: leadDetails?.lead?.generated_id,
      }),
    );
  };

  const handlePreviousQuoteSelect = async (quote: any) => {
    setSelectedPreviousQuote(quote);

    // Close dropdown immediately
    setPreviousQuoteOpen(false);

    dispatch(
      getLeadEstimateLinkAction({
        leadId: leadDetails?.lead?.generated_id,
        estimateId: quote?.quoteId,
        type: "pdf",
        screenType,
      }),
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Deposit button — quotation only */}
      {screenType === "quotation" && (
        <>
          {invoiceModel && (
            <ManageDepositInvoice
              title="Deposit Invoice"
              open={invoiceModel}
              onHide={() => setInvoiceModel(false)}
              invoiceType="deposit"
              isSetLeadDetails={false}
              isNewInvoice={false}
              selectedQuote={estimateForm}
            />
          )}
          <CustomButton
            style={{ marginRight: "10px" }}
            title="Deposit"
            disabled={!estimateForm?.quoteId || leadDetails?.lead?.status === "STORAGE"}
            onClick={() => setInvoiceModel(true)}
          />
        </>
      )}

      {/* Accept */}
      <CustomButton
        disabled={
          !estimateForm?.quoteId || leadDetails?.lead?.status === "STORAGE" ||
          (disabledField && !estimateForm?.is_reaccept_required) ||
          (estimateForm?.accepted_status?.toLowerCase() === "accepted" &&
            !estimateForm?.is_reaccept_required) ||
          estimateForm?.accepted_status?.toLowerCase() === "initiated" 
        }
        style={{ marginRight: "10px" }}
        title={
          estimateForm?.accepted_status?.toLowerCase() === "accepted"
            ? estimateForm?.is_reaccept_required
              ? "Re-Accept"
              : "Accepted"
            : "Accept"
        }
        onClick={handleAcceptQuote}
      />

      {/* Duplicate */}
      <CustomButton
        disabled={
          !estimateForm?.quoteId || leadDetails?.lead?.status === "COMPLETED" || leadDetails?.lead?.status === "STORAGE"
          // leadDetails?.lead?.status === "JOB"
        }
        style={{ marginRight: "10px" }}
        title="Duplicate"
        onClick={handleDuplicateTab}
      />

      {/* ── Email ──────────────────────────────────────────────────────────────
          Professional Quotes → SplitTemplateButton (template picker)
          Non-professional    → plain CustomButton  (standard email API)
      ────────────────────────────────────────────────────────────────────────── */}
      {isProfessional ? (
        <SplitTemplateButton
          title="Email"
          options={allEmailTemplates}
          disabled={(disabledField && !estimateForm?.is_reaccept_required) || leadDetails?.lead?.status === "STORAGE"}
          onButtonClick={(
            event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
          ) => {
            // Only trigger if not the dropdown toggle
            if (event && event.nativeEvent instanceof PointerEvent) {
              // If the button is the dropdown (has aria-haspopup), do nothing
              if ((event.target as HTMLElement).getAttribute("aria-haspopup")) {
                return;
              }
            }
            // Find the default template
            let defaultTemplate = allEmailTemplates.find(
              (tpl: any) => tpl.isDefault,
            );
            if (
              !defaultTemplate &&
              allEmailTemplates &&
              allEmailTemplates.length > 0
            ) {
              // If no isDefault, use the first template
              defaultTemplate = allEmailTemplates[0];
            }
            if (defaultTemplate) {
              setSelectedTemplate(defaultTemplate);
              onShow("email", defaultTemplate);
            }
          }}
          onSelect={(template: any) => {
            console.log("Selected Template:", template);
            setSelectedTemplate(template);
            onShow("email", template);
            // handleSendEmailWithTemplate(template);
          }}
        />
      ) : (
        <CustomButton
          style={{ marginRight: "10px" }}
          title="Email"
           disabled={leadDetails?.lead?.status === "STORAGE" ? true : false}
          onClick={() => onShow("email")}
        />
      )}

      {/* PDF */}
      <CustomButton
        style={{ marginRight: "10px" }}
        title="PDF"
        onClick={() => onShow("pdf")}
        disabled={leadDetails?.lead?.status === "STORAGE" ? true : false}
      />

      {/* ODT */}
      <CustomButton
        style={{ marginRight: "10px" }}
        title="ODT"
        onClick={() => onShow("odt")}
         disabled={leadDetails?.lead?.status === "STORAGE" ? true : false}
      />

      {screenType === "quotation" && (
        <div
          ref={previousQuoteRef}
          style={{
            position: "relative",
            display: "inline-block",
            float: "right",
            marginRight: "20px",
            marginTop: "5px",
          }}
        >
          <div ref={previousQuoteBtnRef}>
            <CustomButton
              disabled={!estimateForm?.quoteId || estimateForm?.count <= 1 || leadDetails?.lead?.status === "STORAGE"}
              style={{ marginRight: "0px" }}
              title={
                <span
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  Previous Quote
                  <span style={{ fontSize: "10px" }}>
                    {previousQuoteOpen ? "▲" : "▼"}
                  </span>
                </span>
              }
              onClick={() =>
                previousQuoteOpen
                  ? setPreviousQuoteOpen(false)
                  : handlePreviousQuoteClick()
              }
            />
          </div>

          {previousQuoteOpen && (
            <div
              style={{
                position: "absolute",
                // ✅ upward ya downward, dono handle
                ...(openUpward
                  ? { bottom: "calc(100% + 6px)" }
                  : { top: "calc(100% + 6px)" }),
                right: 0,
                width: "max-content",
                minWidth: "100%",
                maxWidth: "400px",
                maxHeight: "260px", // ✅ fix: ab list clip nahi hogi
                overflowY: "auto", // ✅ fix: scroll enable
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                zIndex: 9999,
              }}
            >
              {previousQuotes.length === 0 ? (
                <div
                  style={{
                    padding: "12px 16px",
                    color: "#637381",
                    fontSize: "14px",
                  }}
                >
                  No previous quotes found.
                </div>
              ) : (
                previousQuotes.map((quote: any, index: number) => {
                  const pqId = quote?.quoteId || quote?.id || quote?.quote_id;
                  const quoteDate =
                    quote?.created_at || quote?.createdAt || quote?.quoteDate;
                  const isSelected =
                    selectedPreviousQuote &&
                    (selectedPreviousQuote?.quoteId ||
                      selectedPreviousQuote?.id ||
                      selectedPreviousQuote?.quote_id) === pqId;

                  return (
                    <button
                      key={pqId || index}
                      type="button"
                      onClick={() => handlePreviousQuoteSelect(quote)}
                      style={{
                        width: "100%",
                        border: "none",
                        background: isSelected ? "#eef4ff" : "#fff",
                        padding: "12px 16px",
                        textAlign: "left",
                        cursor: "pointer",
                        borderBottom:
                          index !== previousQuotes.length - 1
                            ? "1px solid #eee"
                            : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isSelected
                          ? "#eef4ff"
                          : "#fff";
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#212B36",
                        }}
                      >
                        Quote{" "}
                        {quote?.quoteNumber || quote?.quote_number || index + 1}
                      </div>

                      {quoteDate && (
                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "12px",
                            color: "#637381",
                          }}
                        >
                          {new Date(quoteDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                      )}

                      {quote?.accepted_status && (
                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "12px",
                            color: "#637381",
                          }}
                        >
                          Status: {quote.accepted_status}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Alert popup (pdf/odt confirm) */}
      <AlertPopup
        open={open}
        onHide={onHide}
        title={
          screenType === "quotation"
            ? popupTitleQuote[actionType]
            : popupTitle[actionType]
        }
        content={popupContent[actionType]}
        submitHandler={submitHandler}
      />

      {/* Warning: save before action */}
      {warningModelShow && (
        <AlertMessage
          open={warningModelShow}
          setAlertModelShow={setWarningModelShow}
          onHide={onHide}
          title={
            screenType === "quotation"
              ? popupTitleQuote[actionType]
              : popupTitle[actionType]
          }
          content={
            saveWarningContent[actionType] ||
            "Please save the quote data before performing this action."
          }
        />
      )}
    </>
  );
};

export default QuoteActions;
